import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ErrorType } from "src/app/enums/error-type.enum";
import endpoints from "src/app/helpers/endpoints";
import { ApiService } from "src/app/services/api.service";
import { MapsService } from "src/app/services/maps.service";
import * as moment from "moment";
import { environment } from 'src/environments/environment';

@Component({
  selector: "fury-transfer-agreement",
  templateUrl: "./transfer-agreement.component.html",
  styleUrls: ["./transfer-agreement.component.scss"],
})
export class TransferAgreementComponent implements OnInit, OnDestroy {
  ngUnsubscribe = new Subject<void>();
  @ViewChild("billingLocation", { static: false })
  public billingLocation!: ElementRef;
  userDetailForm = new FormGroup({
    firstName: new FormControl("", [Validators.required]),
    lastName: new FormControl("", [Validators.required]),
    emailAddress: new FormControl("", [
      Validators.required,
      Validators.pattern(/^[\w.-]+@[\w.-]+\.\w{2,3}$/),
    ]),
    phoneNumber: new FormControl("", [
      Validators.required,
      Validators.pattern(/^[2-9][0-8]\d{1}[2-9]\d{2}\d{4}$/),
    ]),
    movedDate: new FormControl("", [Validators.required]),
  });
  coveredAddressForm = new FormGroup({
    streetAddress: new FormControl("", [Validators.required]),
    city: new FormControl("", [Validators.required]),
    state: new FormControl("", [Validators.required]),
    zip: new FormControl("", [Validators.required]),
    unitNumber: new FormControl(""),
    movedDate: new FormControl("", [Validators.required]),
  });
  userFormStep = 1;
  minData = new Date().toISOString().split("T")[0];
  tabNameEnum = {
    0: "Update Agreement Holder",
    1: "Update Covered Address",
  };
  selectedTab = this.tabNameEnum[0];
  customerDetails: any = null;
  assetsUrl = environment.assetsUrl + 'images/';
  isLoading = false;
  isFetchingUserDetails = false;

  constructor(
    public dialogRef: MatDialogRef<TransferAgreementComponent>,
    private apiService: ApiService,
    @Inject(MAT_DIALOG_DATA) public agreementId: string,
    private mapsService: MapsService
  ) {}

  ngOnInit(): void {}
  close() {
    this.dialogRef.close();
  }
  onTabChange(event: MatTabChangeEvent) {
    this.selectedTab = this.tabNameEnum[event.index];
  }
  getConsumerDetailByEmail() {
    if (this.userDetailForm.get("emailAddress").valid) {
      this.isFetchingUserDetails = true;
      this.apiService
        .get(
          endpoints.customerDashboard.getConsumerDetailByEmail(
            this.userDetailForm.value.emailAddress
          )
        )
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (res: HttpResponse<any>) => {
            if (res.body) {
              this.isFetchingUserDetails = false;
              this.customerDetails = res.body.user;
              this.userDetailForm.patchValue({
                firstName: this.customerDetails?.firstName,
                lastName: this.customerDetails?.lastName,
                phoneNumber: this.customerDetails?.phoneNumber,
              });
              this.userFormStep = 2;
            }
          },
          (err: HttpErrorResponse) => {
            this.isFetchingUserDetails = false;
            this.customerDetails = null;
            this.userDetailForm.get("firstName").setValue("");
            this.userDetailForm.get("lastName").setValue("");
            this.userDetailForm.get("phoneNumber").setValue("");
            this.userFormStep = 2;
          }
        );
    } else {
      this.userDetailForm.get("emailAddress").markAsTouched();
    }
  }
  transferAgreement() {
    if (
      this.selectedTab === this.tabNameEnum[0]
        ? this.userDetailForm.valid
        : this.coveredAddressForm.valid
    ) {
      this.isLoading = true;
      const payload = this.createPayload();
      this.apiService
        .update(
          endpoints.customerDashboard.transferAgreement(this.agreementId),
          payload
        )
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (res: HttpResponse<any>) => {
            this.apiService.alert(res.body.message, ErrorType.Success);
            this.isLoading = false;
            this.dialogRef.close(res.body);
          },
          error: (err: HttpErrorResponse) => {
            this.isLoading = false;
            this.apiService.alert(err.message, ErrorType.Error);
          },
        });
    } else {
      this.selectedTab === this.tabNameEnum[0]
        ? this.userDetailForm.markAllAsTouched()
        : this.coveredAddressForm.markAllAsTouched();
    }
  }
  createPayload() {
    const payload: any = {};
    if (this.selectedTab === this.tabNameEnum[0]) {
      payload.userDetail = {
        firstName: this.userDetailForm.value.firstName,
        lastName: this.userDetailForm.value.lastName,
        emailAddress: this.userDetailForm.value.emailAddress,
        phoneNumber: this.userDetailForm.value.phoneNumber,
      };
      payload.movedDate = moment(this.userDetailForm.value.movedDate).format(
        "YYYY-MM-DD"
      );
      payload.updateAgreementHolder = true;
      payload.updateCoveredAddress = false;
      return payload;
    }
    if (this.selectedTab === this.tabNameEnum[1]) {
      payload.coveredAddress = {
        streetAddress: this.coveredAddressForm.value.streetAddress,
        city: this.coveredAddressForm.value.city,
        state: this.coveredAddressForm.value.state,
        zip: this.coveredAddressForm.value.zip,
        unitNumber: this.coveredAddressForm.value.unitNumber,
      };
      payload.movedDate = moment(
        this.coveredAddressForm.value.movedDate
      ).format("YYYY-MM-DD");
      payload.updateAgreementHolder = false;
      payload.updateCoveredAddress = true;
      return payload;
    }
    return;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.billingLocation)
        this.mapsService.autoFillLocation(
          this.billingLocation,
          "billingLocation"
        );
    }, 200);

    this.mapsService.getaddressEvent
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {
        if (res && typeof res === "object" && res.root === "billingLocation") {
          this.coveredAddressForm.patchValue({
            streetAddress: res?.address,
            state: res?.city,
            city: res?.state,
            zip: res?.zipcode,
          });
        }
      });
  }

  checkZipCode(event: any) {
    if (this.coveredAddressForm?.value?.zip?.length !== 5) {
      return;
    }
    this.apiService
      .getCityState(this.coveredAddressForm.controls["zip"])
      .then((data) => {
        if (data) {
          this.coveredAddressForm.patchValue(data);
        } else {
          this.coveredAddressForm.controls["zip"].setErrors({ notValid: true });
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
